import { useCallback, useEffect, useState } from 'react';
import {
  LogOut,
  Layers,
  GitCompare,
  BarChart3,
  BarChart2,
  FileText,
  Database,
  AlertTriangle,
  Users,
  ClipboardCheck,
  Plug,
  CheckCircle2,
  XCircle,
  Loader2,
  Settings,
  CircleHelp,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  CalendarDays,
  ShieldCheck,
} from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { getUiTheme, UI_THEME_EVENT } from './uiTheme';
import ToolLogin from './ToolLogin';
import MappingCatalog from './MappingCatalog';
import PayrollRecon from './PayrollRecon';
import ReconResults from './ReconResults';
import Connections from './Connections';
import IntegrationErrors from './IntegrationErrors';
import WageTypeTotals from './WageTypeTotals';
import Reports from './Reports';
import DemographicRecon from './DemographicRecon';
import PrePayroll from './PrePayroll';
import SettingsPage from './Settings';
import PayrollPeriods from './PayrollPeriods';
import HelpCenter from './HelpCenter';
import AdminPanel from './AdminPanel';
import LegalDisclaimer from './LegalDisclaimer';
import AppLogo from './AppLogo';
import WorkflowGuide, { dismissWorkflowGuide, isWorkflowGuideDismissed } from './WorkflowGuide';
import OnboardingWizard, { isOnboardingComplete } from './OnboardingWizard';
import { apiFetch, getApiBase, getCsrfToken } from './apiBase';

const SIDEBAR_MIN_KEY = 'sidebarMinimized';

const NAV_GROUPS = [
  {
    label: 'Payroll',
    items: [
      { id: 'periods', label: 'Payroll periods', icon: CalendarDays },
      { id: 'recon', label: 'Payroll Recon', icon: GitCompare },
      { id: 'prepayroll', label: 'Pre-Payroll', icon: ClipboardCheck },
      { id: 'demographics', label: 'Demographic Recon', icon: Users },
      { id: 'results', label: 'Results', icon: BarChart3 },
    ],
  },
  {
    label: 'Data & files',
    items: [
      { id: 'reports', label: 'Reports', icon: FileText },
      { id: 'wageTotals', label: 'Wage Type Totals', icon: BarChart2 },
      { id: 'errors', label: 'Errors', icon: AlertTriangle },
    ],
  },
  {
    label: 'Setup',
    items: [
      { id: 'mappings', label: 'Mapping Studio', icon: Layers },
      { id: 'connections', label: 'Connections', icon: Database },
      { id: 'settings', label: 'Settings', icon: Settings },
      { id: 'help', label: 'Help & product', icon: CircleHelp },
    ],
  },
];

// Shown only to admins (appended to the nav when the session role is admin).
const ADMIN_GROUP = {
  label: 'Administration',
  items: [{ id: 'admin', label: 'Admin', icon: ShieldCheck }],
};

function ConnectionBadge({ connection, testResult, testing, onTest, onManage, compact = false }) {
  const configured = !!connection?.isConfigured;
  const tested = testResult != null;
  const ok = tested && testResult.success;
  const failed = tested && !testResult.success;
  const Icon = !configured || failed ? XCircle : ok ? CheckCircle2 : Plug;
  const tone = !configured || failed ? 'text-rose-400' : ok ? 'text-emerald-400' : 'text-cyan-400';

  const label = configured
    ? connection.source === 'db'
      ? connection.name
      : connection.description || connection.baseUrl?.replace(/^https?:\/\//, '') || 'Default connection'
    : 'Not connected';

  const title = configured
    ? `HCM: ${label}${connection.username ? ` (${connection.username})` : ''}${
        testResult?.message ? ` — ${testResult.message}` : ''
      }. Open Connections.`
    : 'HCM not connected. Open Connections.';

  if (compact) {
    return (
      <button
        type="button"
        onClick={onManage}
        onDoubleClick={configured ? onTest : undefined}
        disabled={testing}
        className="nav-sidebar-item"
        title={title}
        aria-label={title}
      >
        {testing ? (
          <Loader2 className="h-4 w-4 shrink-0 animate-spin text-cyan-400" />
        ) : (
          <Icon className={`h-4 w-4 shrink-0 ${tone}`} />
        )}
      </button>
    );
  }

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800/60 px-3 py-2.5 text-xs">
      <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <Icon className={`h-3.5 w-3.5 shrink-0 ${tone}`} />
        HCM connection
        {configured && connection.source === 'db' && (
          <span className="ml-auto rounded bg-emerald-950/80 px-1.5 py-0.5 text-[9px] text-emerald-400 ring-1 ring-emerald-700/50">
            active
          </span>
        )}
        {configured && connection.source === 'env' && (
          <span className="ml-auto rounded bg-slate-700 px-1.5 py-0.5 text-[9px] text-slate-300">Default</span>
        )}
      </div>
      {configured ? (
        <>
          <div className="truncate font-medium text-slate-200" title={connection.baseUrl}>
            {label}
          </div>
          <div className="truncate text-slate-500">{connection.username}</div>
        </>
      ) : (
        <div className="text-rose-400">Not connected</div>
      )}
      <div className="mt-2 flex gap-1">
        <button
          type="button"
          onClick={onTest}
          disabled={testing || !configured}
          className="rounded border border-slate-600 bg-slate-700 px-2 py-0.5 text-[10px] text-slate-200 hover:bg-slate-600 disabled:opacity-50"
        >
          {testing ? <Loader2 className="inline h-3 w-3 animate-spin" /> : 'Test'}
        </button>
        <button
          type="button"
          onClick={onManage}
          className="rounded border border-slate-600 bg-slate-700 px-2 py-0.5 text-[10px] text-slate-200 hover:bg-slate-600"
        >
          Manage
        </button>
      </div>
    </div>
  );
}

export default function App() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [view, setView] = useState('recon');
  const [hcmConn, setHcmConn] = useState(null);
  const [connTesting, setConnTesting] = useState(false);
  const [connTestResult, setConnTestResult] = useState(null);
  const [connRefresh, setConnRefresh] = useState(0);
  const [resultsRefresh, setResultsRefresh] = useState(0);
  const [uiTheme, setUiTheme] = useState(getUiTheme);
  const [sidebarMinimized, setSidebarMinimized] = useState(
    () => localStorage.getItem(SIDEBAR_MIN_KEY) === '1'
  );
  const [workflowDismissed, setWorkflowDismissed] = useState(isWorkflowGuideDismissed);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    const onTheme = () => setUiTheme(getUiTheme());
    window.addEventListener(UI_THEME_EVENT, onTheme);
    return () => window.removeEventListener(UI_THEME_EVENT, onTheme);
  }, []);

  const refreshActiveConnection = useCallback(async () => {
    try {
      const c = await apiFetch('/api/hcm/connection');
      setHcmConn(c);
      setConnTestResult(null);
    } catch (_) {
      setHcmConn(null);
    }
  }, []);

  const checkSession = useCallback(async () => {
    try {
      const data = await apiFetch('/auth/me');
      if (data.toolUser?.id) {
        setUser(data.toolUser);
        await refreshActiveConnection();
        if (!isOnboardingComplete()) setShowOnboarding(true);
      }
    } catch (_) {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, [refreshActiveConnection]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (user) refreshActiveConnection();
  }, [connRefresh, user, refreshActiveConnection]);

  const logout = async () => {
    const base = getApiBase();
    await fetch(`${base}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'X-XSRF-TOKEN': getCsrfToken() },
    });
    setUser(null);
    setHcmConn(null);
  };

  const testConnection = async () => {
    setConnTesting(true);
    try {
      const r = await apiFetch('/api/hcm/test', { method: 'POST' });
      setConnTestResult(r);
    } catch (e) {
      setConnTestResult({ success: false, message: e.message });
    } finally {
      setConnTesting(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarMinimized((minimized) => {
      const next = !minimized;
      localStorage.setItem(SIDEBAR_MIN_KEY, next ? '1' : '0');
      return next;
    });
  };

  if (checking) {
    return (
      <div className="app-shell relative flex min-h-screen items-center justify-center text-slate-400">
        <div className="app-shell-backdrop" aria-hidden="true" />
        <span className="relative z-10">Loading…</span>
      </div>
    );
  }

  if (!user) {
    return <ToolLogin onSuccess={(u) => setUser(u)} uiTheme={uiTheme} onThemeChange={setUiTheme} />;
  }

  return (
    <div className="app-shell relative min-h-screen">
      <div className="app-shell-backdrop" aria-hidden="true" />
      <div className="app-layout">
      <aside
        className={`app-sidebar flex flex-col md:relative ${
          sidebarMinimized ? 'app-sidebar-minimized' : 'app-sidebar-expanded'
        } ${mobileNavOpen ? 'app-sidebar-mobile-open' : 'app-sidebar-mobile-hidden md:translate-x-0'}`}
      >
        <div className="app-sidebar-header border-b px-2 py-3">
          {sidebarMinimized ? (
            <div className="flex flex-col items-center gap-1">
              <span title="ADP Oracle Reconcile">
                <AppLogo size={28} className="shrink-0" />
              </span>
              <button
                type="button"
                onClick={toggleSidebar}
                className="nav-sidebar-item"
                title="Expand sidebar"
                aria-label="Expand sidebar"
              >
                <PanelLeftOpen className="h-4 w-4 shrink-0" />
              </button>
            </div>
          ) : (
            <div className="flex min-w-0 items-center gap-2 px-1">
              <AppLogo size={32} className="shrink-0" />
              <h1 className="min-w-0 flex-1 text-xs font-bold leading-none text-slate-100 whitespace-nowrap">
                ADP Oracle Reconcile
              </h1>
              <button
                type="button"
                onClick={toggleSidebar}
                className="nav-sidebar-item shrink-0 !w-auto !p-2"
                title="Minimize sidebar"
                aria-label="Minimize sidebar"
              >
                <PanelLeftClose className="h-4 w-4 shrink-0" />
              </button>
            </div>
          )}
        </div>

        <nav
          className={`flex flex-1 flex-col overflow-y-auto py-2 ${
            sidebarMinimized ? 'gap-0.5 px-1.5' : 'gap-0 px-1'
          }`}
        >
          {(user?.role === 'admin' ? [...NAV_GROUPS, ADMIN_GROUP] : NAV_GROUPS).map((group) => (
            <div key={group.label}>
              {!sidebarMinimized && <div className="nav-group-label">{group.label}</div>}
              <div className={`flex flex-col ${sidebarMinimized ? 'gap-0.5' : 'gap-0.5 px-2'}`}>
                {group.items.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => {
                      setView(id);
                      setMobileNavOpen(false);
                    }}
                    title={sidebarMinimized ? label : undefined}
                    aria-label={label}
                    aria-current={view === id ? 'page' : undefined}
                    className={view === id ? 'nav-sidebar-item-active' : 'nav-sidebar-item'}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {!sidebarMinimized && <span className="truncate">{label}</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        <div
          className={`app-sidebar-footer flex flex-col border-t ${
            sidebarMinimized ? 'gap-0.5 px-1.5 py-2' : 'gap-3 px-3 py-4'
          }`}
        >
          <ThemeToggle compact={sidebarMinimized} theme={uiTheme} onChange={setUiTheme} />
          <ConnectionBadge
            compact={sidebarMinimized}
            connection={hcmConn}
            testResult={connTestResult}
            testing={connTesting}
            onTest={testConnection}
            onManage={() => setView('connections')}
          />
          {sidebarMinimized ? (
            <button
              type="button"
              onClick={logout}
              className="nav-sidebar-item"
              title={`Log out (${user.username})`}
              aria-label={`Log out (${user.username})`}
            >
              <LogOut className="h-4 w-4 shrink-0" />
            </button>
          ) : (
            <div className="flex items-center justify-between gap-2 px-1">
              <span className="truncate text-xs text-slate-400" title={user.username}>
                {user.username}
              </span>
              <button type="button" onClick={logout} className="btn-secondary shrink-0 py-1 text-xs">
                <LogOut className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {mobileNavOpen && (
        <button
          type="button"
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          aria-label="Close menu"
          onClick={() => setMobileNavOpen(false)}
        />
      )}

      <div className="app-main">
        <div className="mobile-topbar">
          <button
            type="button"
            onClick={() => setMobileNavOpen(true)}
            className="nav-sidebar-item !w-auto shrink-0"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <span className="truncate text-sm font-semibold text-slate-100">
            {(user?.role === 'admin' ? [...NAV_GROUPS, ADMIN_GROUP] : NAV_GROUPS)
              .flatMap((g) => g.items)
              .find((v) => v.id === view)?.label || 'ADP Oracle Reconcile'}
          </span>
        </div>
        <main className="flex-1 overflow-x-auto px-4 py-6 sm:px-6 lg:px-8">
          <WorkflowGuide
            currentView={view}
            onNavigate={setView}
            hcmConfigured={!!hcmConn?.isConfigured}
            dismissed={workflowDismissed}
            onDismiss={() => {
              dismissWorkflowGuide();
              setWorkflowDismissed(true);
            }}
          />
          {view === 'periods' && <PayrollPeriods onNavigate={setView} />}
          {view === 'recon' && (
            <PayrollRecon
              onRunComplete={() => {
                setResultsRefresh((k) => k + 1);
                setView('results');
              }}
              onNavigate={setView}
            />
          )}
          {view === 'prepayroll' && <PrePayroll onNavigate={setView} />}
          {view === 'demographics' && <DemographicRecon />}
          {view === 'wageTotals' && <WageTypeTotals />}
          {view === 'reports' && <Reports connectionRefresh={connRefresh} />}
          {view === 'results' && (
            <ReconResults refreshKey={resultsRefresh} onNavigate={setView} />
          )}
          {view === 'errors' && <IntegrationErrors />}
          {view === 'mappings' && <MappingCatalog />}
          {view === 'settings' && <SettingsPage onNavigate={setView} />}
          {view === 'admin' && user?.role === 'admin' && <AdminPanel />}
          {view === 'help' && <HelpCenter onNavigate={setView} />}
          {view === 'connections' && (
            <Connections
              onActiveChanged={() => {
                setConnRefresh((k) => k + 1);
                refreshActiveConnection();
              }}
            />
          )}
          <footer className="mt-10 border-t border-slate-700/80 pt-4 pb-2">
            <LegalDisclaimer compact />
          </footer>
        </main>
      </div>
      </div>
      {showOnboarding && (
        <OnboardingWizard
          onNavigate={(id) => {
            setView(id);
            setMobileNavOpen(false);
          }}
          onComplete={() => setShowOnboarding(false)}
        />
      )}
    </div>
  );
}
